import React from 'react';
import Select from 'react-select';
import { DatePicker, InputNumber, TimePicker } from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { brand, gray, ink, white } from '../theme';

const { RangePicker } = DatePicker;

/**
 * Input wrappers for the redesigned form.
 *
 * These began as `reuse/FormControls.js` with the `<InputError />` tooltip marker removed —
 * in this layout the message is rendered inline beneath the control by `ui/Field.js`, so the
 * tooltip would be a second, redundant indicator sitting on top of it. `FormControls.js`
 * itself is untouched; the old modal still uses it.
 *
 * They have since diverged in one more way, and it is the important one: these controls no
 * longer carry Bootstrap's `form-control`. They carry `.pf-control`, defined in
 * `programForm.css`. That class is the whole point — `form-control` lands on a different
 * element in each widget and so produced three different box heights across one form. The
 * long version of that story is in the stylesheet.
 *
 * Two consequences to keep in mind when adding a control here:
 *
 *   - Anything that should look like a field needs `cls()`, which is now the only thing that
 *     applies the box.
 *   - react-select is the exception. It is styled through its own `styles` prop below rather
 *     than by a class, because it renders its border on an inner element that a class on the
 *     component cannot reach cleanly.
 */

const cls = error => `pf-control ${error ? 'is-invalid' : ''}`;

/**
 * Menus render in a portal at `document.body` rather than in place.
 *
 * Two ancestors clip them otherwise: `SectionCard`'s collapse wrapper, which needs
 * `overflow: hidden` while it animates, and `.modal-body`, which scrolls. A menu opening
 * near the bottom of a section was cut off by both. Portalling sidesteps the containment
 * question entirely instead of trying to keep every ancestor unclipped.
 *
 * 1060 clears Bootstrap's modal (1050). The antd half of this pairs with
 * `.program-form-popup` in `programForm.css` — antd takes a class, react-select a style.
 */
const POPUP_Z_INDEX = 1060;
const POPUP_CLASS = 'program-form-popup';
const toBody = () => document.body;

/**
 * react-select's class prefix.
 *
 * Deliberately not the app-wide `cm_select`. `main.css` styles that prefix globally — it
 * strips the control's border with `!important`, shifts it up 5px, and adds a second 12px of
 * text inset (330-340, 12994-13004) — and those rules were written for the boxes the rest of
 * the app draws. Owning a private prefix is what lets the `styles` object below describe this
 * form's control outright, with no `!important` and no reach into the old modal.
 */
const SELECT_PREFIX = 'pf_select';

/**
 * Close an open menu when anything *other than the menu itself* scrolls.
 *
 * This has to be the function form. Passing `closeMenuOnScroll` as a boolean makes
 * react-select close only when the scroll target is the documentElement, body or window
 * (`react-select/src/Select.js:1011` via `isDocumentElement`) — and this form scrolls inside
 * `div.modal-body`, which is none of those, so the listener fired and did nothing. The
 * function form is consulted for every scroll event, wherever it came from.
 *
 * The menu's own list must be exempt, or picking from a long list would dismiss it.
 */
const closeOnOutsideScroll = event => {
    const { target } = event;
    if (!(target instanceof HTMLElement)) return true;
    return !target.closest(`.${SELECT_PREFIX}__menu, .${SELECT_PREFIX}__menu-portal`);
};

/**
 * Defined once: a fresh `styles` object on every render would re-run react-select's emotion
 * pipeline for no reason.
 *
 * These values mirror `.pf-control` in `programForm.css` — 38px tall, 6px radius, gray[300]
 * border, 14px text, brand focus ring — so a react-select sits flush with the plain inputs
 * beside it. Change one and change the other.
 *
 * It is done here rather than in the stylesheet for two reasons. The menu renders in a portal
 * at `document.body`, outside `.program-form`, so a scoped CSS rule cannot reach it at all.
 * And react-select draws its border on an inner element, which is exactly the mismatch that
 * made this control 52px tall when it wore Bootstrap's `form-control`.
 *
 * `state.selectProps.error` is the caller's `error` prop: `SelectInput` spreads the whole
 * prop bag onto `<Select>`, and react-select hands unknown props back through `selectProps`.
 */
const borderFor = state => {
    if (state.selectProps.error) return '#dc3545';
    return state.isFocused ? brand[600] : gray[300];
};

const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: POPUP_Z_INDEX }),
    // `minHeight`, not `height`: an isMulti control has to grow as chips are added.
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        backgroundColor: state.isDisabled ? gray[50] : white,
        borderColor: borderFor(state),
        borderRadius: '6px',
        boxShadow: state.isFocused ? `0 0 0 2px ${brand[200]}` : 'none',
        fontSize: '14px',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        ':hover': { borderColor: borderFor(state) },
    }),
    valueContainer: base => ({ ...base, padding: '0 12px' }),
    input: base => ({ ...base, margin: 0, paddingTop: 0, paddingBottom: 0 }),
    placeholder: base => ({ ...base, color: ink[400] }),
    singleValue: base => ({ ...base, color: ink[800] }),
    // The stock separator reads as a second border inside the box.
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: base => ({ ...base, padding: '0 10px', color: ink[400] }),
    clearIndicator: base => ({ ...base, padding: '0 4px', color: ink[400] }),
    multiValue: base => ({ ...base, backgroundColor: gray[100], borderRadius: '4px' }),
    multiValueLabel: base => ({ ...base, color: ink[800], fontSize: '13px' }),
    menu: base => ({ ...base, fontSize: '14px', borderRadius: '6px', zIndex: POPUP_Z_INDEX }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? brand[600]
            : state.isFocused
                ? brand[50]
                : 'transparent',
        color: state.isSelected ? white : ink[800],
        ':active': {
            backgroundColor: state.isSelected ? brand[600] : brand[100],
        },
    }),
};

export const TextInput = p => (
    <input
        type="text"
        id={p.id}
        name={p.name}
        value={p.value}
        onChange={p.change}
        placeholder={p.pHolder}
        readOnly={p.readOnly}
        disabled={p.disabled}
        maxLength={p.maxLength}
        className={cls(p.error)}
    />
);

export const TextareaInput = p => (
    <textarea
        id={p.id}
        name={p.name}
        value={p.value}
        onChange={p.change}
        placeholder={p.pHolder}
        rows={p.rows}
        className={cls(p.error)}
    />
);

export const NumberInput = p => (
    <InputNumber
        id={p.id}
        onChange={p.change}
        value={p.value}
        placeholder={p.pHolder}
        className={cls(p.error)}
        {...p}
    />
);

/**
 * Clearing a selection hands the change handler `null` — from the ×, and from backspace on a
 * focused control, which `backspaceRemovesValue` enables once `isClearable` is on.
 *
 * The multi handlers already expect that: `handleMultiSelect` reads `e ? e.map(i => i.value) : ''`,
 * and react-select has always passed `null` when the last chip is removed
 * (`react-select/src/Select.js:710`). Those pass straight through, unchanged.
 *
 * The single-value handlers cannot take it. Every one of them dereferences the option — `e.value`
 * throughout, and `e.meals` in `sections/Amenities.js` — and `setDistrict`'s result is later read
 * as `district.value` when the payload is assembled (`ProgramForm/index.js:441`), so even storing
 * the `null` would throw at save time rather than on the click. They get this empty option
 * instead, which lands each of them on exactly the state the form starts in: `''` for the
 * `userInput` keys, and a falsy `district.value` that the payload already skips.
 *
 * Normalising here rather than in the seventeen handlers keeps it one decision, and keeps the
 * contract these call sites were written against — `change` is handed an option, never a null.
 */
const EMPTY_OPTION = { value: '' };

const clearSafe = p => option => p.change(p.isMulti ? option : option || EMPTY_OPTION);

/**
 * The form's one and only dropdown. Every select in every section goes through here.
 *
 * That uniformity is the point, and it was arrived at the hard way. A native `<select>`
 * carrying `.pf-control` is the cheaper control and matches the inputs beside it perfectly
 * while closed — but its *open* menu is drawn by the operating system, with the OS highlight
 * colour and no styling hooks worth relying on. Mixing the two gave a form whose closed boxes
 * agreed and whose open menus did not.
 *
 * Going the other way — native everywhere — is not available: four fields are `isMulti`, and
 * `<select multiple>` is a genuinely poor control, while the timezone list is ~600 entries and
 * the state list 58, both of which want type-to-filter. So consistency has to come from
 * react-select, and it has to be used even where it is more than the field needs.
 *
 * If a dropdown ever does need to be native, the whole form goes with it.
 *
 * No `className`: the box comes from `selectStyles`, not `.pf-control`. See the note there.
 */
export const SelectInput = p => (
    <Select
        inputId={p.id}
        placeholder={p.pHolder}
        onChange={clearSafe(p)}
        // Without this react-select derives it from `isMulti` (`react-select/src/Select.js:895`),
        // so the four multi fields could be emptied and the other eighteen could not: once a
        // value was picked the only way back to none was to abandon the form. The prototype and
        // the staff application both allow it — their native `<select>` keeps the placeholder
        // `<option>` selectable — and this is react-select's equivalent affordance.
        isClearable
        classNamePrefix={SELECT_PREFIX}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        // A portalled menu is positioned once, against the viewport, so it stays put while
        // `.modal-body` scrolls out from under it and ends up detached from its control.
        // react-select listens for scroll in the capture phase, which catches the modal's
        // own scrolling, and closes rather than trying to chase it.
        closeMenuOnScroll={closeOnOutsideScroll}
        styles={selectStyles}
        {...p}
    />
);

export const DateInput = p => (
    <DatePicker
        id={p.id}
        getPopupContainer={toBody}
        dropdownClassName={POPUP_CLASS}
        onChange={p.change}
        className={cls(p.error)}
        {...p}
    />
);

export const RangeInput = p => (
    <RangePicker
        id={p.id}
        getPopupContainer={toBody}
        dropdownClassName={POPUP_CLASS}
        onChange={p.change}
        className={cls(p.error)}
        {...p}
    />
);

export const TimeInput = p => (
    <TimePicker
        id={p.id}
        getPopupContainer={toBody}
        dropdownClassName={POPUP_CLASS}
        onChange={p.change}
        className={cls(p.error)}
        {...p}
    />
);

/**
 * Rich text. The value round-trips through `addBr`/`getBr` at the payload boundary, not
 * here — the editor sees plain HTML either way.
 */
export const RichText = p => (
    <div className={p.error ? 'is-invalid' : ''}>
        <ReactQuill
            id={p.id}
            theme="snow"
            value={p.value || ''}
            placeholder={p.pHolder}
            onChange={p.change}
        />
    </div>
);
